import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../hooks/AuthContext';
import ChatApp from '../components/ChatApp';
import './AdminUsers.css';

const formatDateTime = (value) => {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

const AdminCreatorStudioPage = () => {
  const { authenticatedUser } = useAuthContext();
  const [state, setState] = useState({
    loading: true,
    error: '',
    data: null,
  });
  const [launching, setLaunching] = useState(false);
  const [launchSpec, setLaunchSpec] = useState(null);

  useEffect(() => {
    if (!authenticatedUser?.token) {
      return undefined;
    }

    const controller = new AbortController();

    const loadStatus = async () => {
      setState({
        loading: true,
        error: '',
        data: null,
      });

      try {
        const response = await fetch('/.netlify/functions/admin-get-creator-studio-status', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authenticatedUser.token}`,
          },
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load Creator Studio status.');
        }

        setState({
          loading: false,
          error: '',
          data,
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          error: error.message || 'Unable to load Creator Studio status.',
          data: null,
        });
      }
    };

    loadStatus();

    return () => controller.abort();
  }, [authenticatedUser]);

  const handleLaunch = async () => {
    if (!authenticatedUser?.token) {
      return;
    }

    setLaunching(true);

    try {
      const response = await fetch('/.netlify/functions/admin-launch-creator-studio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authenticatedUser.token}`,
        },
        body: JSON.stringify({ mode: 'text' }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.launch_spec?.gamePackId) {
        throw new Error(data?.message || 'Unable to launch Creator Studio.');
      }

      setLaunchSpec(data.launch_spec);
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message || 'Unable to launch Creator Studio.',
      }));
    } finally {
      setLaunching(false);
    }
  };

  const creatorStudio = state.data?.creator_studio;

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Creator Studio Access</p>
          <h2 className="cq-admin-page__title">Creator Studio</h2>
          <p className="cq-admin-page__copy">
            Create and refine game content using the AI-powered Creator Studio.
            Build new questions, shape game behavior, and expand Quest Packs that power the Cringe Quest experience.
          </p>
        </div>
      </header>

      {state.loading ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Loading Creator Studio status...</p>
        </div>
      ) : null}

      {!state.loading && state.error ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">{state.error}</p>
        </div>
      ) : null}

      {!state.loading && !state.error && creatorStudio ? (
        <div className="cq-admin-creator-studio-grid">
          <section className="cq-admin-panel">
            <h3 className="cq-admin-section-title">Status</h3>
            <dl className="cq-admin-definition-list">
              <div><dt>Game Pack Name</dt><dd>{creatorStudio.name}</dd></div>
              <div><dt>Game Pack ID</dt><dd className="cq-admin-cell-id">{creatorStudio.game_pack_id}</dd></div>
              <div><dt>Status</dt><dd>{creatorStudio.status}</dd></div>
              <div><dt>Admin-only Access</dt><dd>{creatorStudio.admin_only ? 'Enabled' : 'Disabled'}</dd></div>
              <div><dt>Associated Host</dt><dd>{creatorStudio.associated_host || 'Not available'}</dd></div>
              <div><dt>Last Updated</dt><dd>{formatDateTime(creatorStudio.last_updated_at)}</dd></div>
            </dl>
          </section>

          <section className="cq-admin-panel">
            {!launchSpec ? (
              <>
                <h3 className="cq-admin-section-title">Launch</h3>
                <p className="cq-admin-page__copy">
                  Start Creator Studio inside this admin workspace. It still uses the existing backend session flow and now defaults to Voice Mode.
                </p>
                <button
                  type="button"
                  className="cq-admin-action-button"
                  disabled={launching || !state.data?.can_launch}
                  onClick={handleLaunch}
                >
                  {launching ? 'Launching Creator Studio...' : 'Open Creator Studio'}
                </button>
              </>
            ) : (
              <div className="cq-admin-embedded-studio">
                <div className="cq-admin-embedded-studio__header">
                  <div>
                    <h3 className="cq-admin-section-title">Creator Studio Workspace</h3>
                    <p className="cq-admin-page__copy">Active inside the Admin module.</p>
                  </div>
                  <button
                    type="button"
                    className="cq-admin-action-button cq-admin-action-button--secondary"
                    onClick={() => setLaunchSpec(null)}
                  >
                    Close Workspace
                  </button>
                </div>
                <div className="cq-admin-embedded-studio__body">
                  <ChatApp
                    embedded
                    embeddedLaunchSpec={launchSpec}
                    onEmbeddedSessionClosed={() => setLaunchSpec(null)}
                  />
                </div>
              </div>
            )}
          </section>
        </div>
      ) : null}

      <section className="cq-admin-panel">
        <h3 className="cq-admin-section-title">Creator Studio Object Model</h3>
        <div className="cq-admin-creator-studio-diagram">
          <img
            src="/images/creator-studio-object-model.png"
            alt="Creator Studio object model diagram for Quest Packs, Game Packs, Hosts, and related content."
            className="cq-admin-creator-studio-diagram__image"
          />
        </div>
      </section>
    </section>
  );
};

export default AdminCreatorStudioPage;
